import { PropsWithChildren, createContext, useCallback, useEffect, useState } from 'react';
import {
  IComposableDynamicListingContext,
  getAllBusinessDefinition,
  getAllLeadStatusByVisibility,
} from 'wisestep-api';

import { ChartScope } from '../../../features/composableAnalytics';
import { postgreBusinessDefinitionKeys } from '../../../features/composableAnalytics/DerivedCharts/businessDefinition/helper/postgreBusinessDefinitionKeys';
import { IBusinessData } from '../../../features/crmSettings/customizeStageStatus/helper';
import { leadChartsByStatusKeys } from '../../composableAnalytics/BaseCharts/lead/leadDashboardKeys';
import { potentialRevenueForBDefKeys } from '../../composableAnalytics/DerivedCharts/PotentialRevenue/helper/potentialRevenueDashboardkeys';
import { StatusList } from '../../leads/components/LeadDetailsForm';
import { ITimePeriod } from '../helper/postgreDrawerChartsList';

export const ComposableDynamicListingContext =
  createContext<IComposableDynamicListingContext | null>(null);

export default function ComposableDynamicListingContextProvider(
  props: PropsWithChildren<IComposableDynamicListingContext | null>
) {
  const [allBusinessDefinition, setAllBusinessDefinition] = useState<IBusinessData[]>();
  const [leadStatusList, setLeadStatusList] = useState<StatusList[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getAllBusinessDefinition();
        setAllBusinessDefinition(
          response.data
            ? response.data
                .slice()
                .sort((a, b) => (a.isArchive === b.isArchive ? 0 : a.isArchive ? 1 : -1))
            : []
        );
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchStatusList = async () => {
      try {
        const response = await getAllLeadStatusByVisibility();
        const statusData: StatusList[] =
          response.data?.map((statusObj) => ({
            label: statusObj.leadStatusName as string,
            value: statusObj.id as string,
          })) ?? [];
        setLeadStatusList(statusData);
      } catch (e) {
        console.error('Error fetching status list:', e);
      }
    };
    fetchStatusList();
  }, []);

  const businessDefinitionChartsKeysPostgre = useCallback(
    (timePeriod?: { id: string; label: string; filterDate: string }, selectedScope?: ChartScope) =>
      allBusinessDefinition
        ? postgreBusinessDefinitionKeys(
            timePeriod ?? { id: '', label: '', filterDate: '' },
            selectedScope ?? ChartScope.WORKSPACE,
            allBusinessDefinition
          )
        : [],
    [allBusinessDefinition]
  );

  const potentialRevenueForBDefChartsKeys = useCallback(
    (timePeriod: ITimePeriod, selectedScope?: ChartScope) =>
      allBusinessDefinition
        ? potentialRevenueForBDefKeys(
            timePeriod,
            selectedScope ?? ChartScope.WORKSPACE,
            allBusinessDefinition
          )
        : [],
    [allBusinessDefinition]
  );

  const leadByStatusChartsKeys = useCallback(
    (timePeriod: ITimePeriod, selectedScope?: ChartScope) =>
      leadStatusList
        ? leadChartsByStatusKeys(timePeriod, selectedScope ?? ChartScope.WORKSPACE, leadStatusList)
        : [],
    [leadStatusList]
  );

  return (
    <ComposableDynamicListingContext.Provider
      value={{
        businessDefinitionChartsKeysPostgre,
        potentialRevenueForBDefChartsKeys,
        leadByStatusChartsKeys,
        allBusinessDefinition,
        leadStatusList,
      }}
    >
      {props.children}
    </ComposableDynamicListingContext.Provider>
  );
}
